import { useState, useEffect, useCallback, useRef } from 'react';
import { Search, TrendingUp, Filter, Eye, Award, ExternalLink, X, Heart, MessageCircle, Calendar, Hash, UserPlus, Check, UserCheck, Plus, RefreshCw, Coins, Infinity } from 'lucide-react';
import CustomListbox from '../components/CustomListbox';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContextSupabase';

interface InfluencerData {
  id: string;
  name: string | null;
  display_name: string | null;
  username: string;
  platform: string;
  followers_count: number;
  engagement_rate: number;
  category: string | null;
  profile_image_url: string | null;
  profile_url: string | null;
  bio: string | null;
  verified: boolean;
  avg_likes: number;
  avg_comments: number;
  avg_views: number;
  posts_count: number;
  collabs_count?: number;
  country: string | null;
  archived: boolean;
}

interface Post {
  id: string;
  post_id: string;
  post_url: string;
  post_type: string;
  caption: string;
  image_url: string | null;
  likes_count: number;
  comments_count: number;
  timestamp: string | null;
  is_collaboration: boolean;
  collaboration_keywords: string[];
  scraped_at: string;
}

export default function Discover() {
  const { currentUser, userProfile } = useAuth();
  const [influencers, setInfluencers] = useState<InfluencerData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [platformFilter, setPlatformFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('followers');
  const [minFollowers, setMinFollowers] = useState<number>(0);
  const [maxFollowers, setMaxFollowers] = useState<number>(10000000);
  const [minEngagement, setMinEngagement] = useState<number>(0);
  const [maxEngagement, setMaxEngagement] = useState<number>(100);
  const [minCollabs, setMinCollabs] = useState<number>(0);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedInfluencer, setSelectedInfluencer] = useState<InfluencerData | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [addedInfluencers, setAddedInfluencers] = useState<Set<string>>(new Set());
  const [recruitedInfluencers, setRecruitedInfluencers] = useState<Set<string>>(new Set());
  const [newProfileUrl, setNewProfileUrl] = useState('');
  const [isScrapingNewProfile, setIsScrapingNewProfile] = useState(false);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [creditsRemaining, setCreditsRemaining] = useState<number>(10);
  const [creditsResetAt, setCreditsResetAt] = useState<string | null>(null);
  const [refreshingInfluencers, setRefreshingInfluencers] = useState<Set<string>>(new Set());
  const [platformTabs, setPlatformTabs] = useState<Record<string, 'instagram' | 'tiktok' | 'youtube' | 'snapchat'>>({});
  const [tiktokProfiles, setTiktokProfiles] = useState<Record<string, any>>({});
  const [youtubeProfiles, setYoutubeProfiles] = useState<Record<string, any>>({});
  const [snapchatProfiles, setSnapchatProfiles] = useState<Record<string, any>>({});

  const PAGE_SIZE = 50;
  const loadingRef = useRef(false);

  const convertToLinear = (logValue: number): number => {
    const minLog = Math.log(1000);
    const maxLog = Math.log(10000000);
    const scale = (logValue - minLog) / (maxLog - minLog);
    return Math.round(Math.exp(minLog + scale * (maxLog - minLog)));
  };

  const convertToLog = (linearValue: number): number => {
    const minLog = Math.log(1000);
    const maxLog = Math.log(10000000);
    const clampedValue = Math.max(1000, Math.min(10000000, linearValue));
    return minLog + (Math.log(clampedValue) - minLog) / (maxLog - minLog) * (maxLog - minLog);
  };

  const loadUserCredits = async () => {
    if (!currentUser || !userProfile?.agency_id) return;

    try {
      const { data, error } = await supabase.rpc('get_organization_credits', {
        org_id: userProfile.agency_id
      });

      if (error) throw error;

      if (data && data.length > 0) {
        setCreditsRemaining(data[0].balance);
      }
    } catch (error) {
      console.error('Error loading user credits:', error);
    }
  };

  const loadAddedInfluencers = async () => {
    if (!currentUser || !userProfile?.agency_id) return;

    try {
      const { data, error } = await supabase
        .from('organization_influencers')
        .select('influencer_id')
        .eq('organization_id', userProfile.agency_id);

      if (error) throw error;

      const influencerIds = new Set(data?.map(item => item.influencer_id) || []);
      setAddedInfluencers(influencerIds);
    } catch (error) {
      console.error('Error loading added influencers:', error);
    }
  };

  const loadRecruitedInfluencers = async () => {
    if (!currentUser || !userProfile?.agency_id) return;

    try {
      const { data, error } = await supabase
        .from('recruitment_influencers')
        .select('influencer_id')
        .eq('agency_id', userProfile.agency_id);

      if (error) throw error;

      const influencerIds = new Set(data?.map(item => item.influencer_id).filter(Boolean) || []);
      setRecruitedInfluencers(influencerIds);
    } catch (error) {
      console.error('Error loading recruited influencers:', error);
    }
  };

  const loadInfluencers = useCallback(async (pageNum = 0, append = false) => {
    if (loadingRef.current) {
      console.log('Already loading, skipping...');
      return;
    }

    try {
      loadingRef.current = true;

      if (pageNum === 0) {
        setIsLoading(true);
        if (!append) {
          setInfluencers([]);
        }
      } else {
        setIsLoadingMore(true);
      }

      const from = pageNum * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      console.log(`Loading page ${pageNum} (${from}-${to})...`);

      const { data, error } = await supabase.rpc('search_influencers', {
        search_term: search,
        platform_filter: platformFilter,
        category_filter: categoryFilter,
        min_followers: minFollowers,
        max_followers: maxFollowers,
        min_engagement: minEngagement,
        max_engagement: maxEngagement,
        page_offset: from,
        page_limit: PAGE_SIZE
      });

      if (error) throw error;

      console.log(`Loaded page ${pageNum}: ${data?.length || 0} influencers`);

      if (!data || data.length < PAGE_SIZE) {
        console.log('No more influencers to load');
        setHasMore(false);
      }

      const influencerIds = (data || []).map(inf => inf.id);

      const { data: collabCounts, error: collabError } = await supabase
        .from('posts')
        .select('influencer_id')
        .in('influencer_id', influencerIds)
        .eq('is_collaboration', true);

      if (collabError) throw collabError;

      const collabCountMap = new Map<string, number>();
      (collabCounts || []).forEach(post => {
        const count = collabCountMap.get(post.influencer_id) || 0;
        collabCountMap.set(post.influencer_id, count + 1);
      });

      const influencersWithCollabs = (data || []).map(influencer => ({
        ...influencer,
        collabs_count: collabCountMap.get(influencer.id) || 0
      }));

      if (append) {
        setInfluencers(prev => {
          const existingIds = new Set(prev.map(inf => inf.id));
          const newInfluencers = influencersWithCollabs.filter(inf => !existingIds.has(inf.id));
          console.log(`Adding ${newInfluencers.length} new influencers (filtered ${influencersWithCollabs.length - newInfluencers.length} duplicates)`);
          return [...prev, ...newInfluencers];
        });
      } else {
        setInfluencers(influencersWithCollabs);
      }

      // Load extra network profiles for these influencers
      const ids = influencerIds;
      if (ids.length > 0) {
        const [ttRes, ytRes, snapRes, spotlightsRes] = await Promise.all([
          supabase.from('tiktok_profiles').select('influencer_id, tiktok_username, followers_count, engagement_rate, avg_views').in('influencer_id', ids),
          supabase.from('youtube_profiles').select('influencer_id, youtube_handle, subscribers_count, engagement_rate, avg_views').in('influencer_id', ids),
          supabase.from('snapchat_profiles').select('influencer_id, username, subscriber_count').in('influencer_id', ids),
          supabase.from('snapchat_spotlights').select('influencer_id, views').in('influencer_id', ids),
        ]);
        if (ttRes.data) {
          const map: Record<string, any> = {};
          ttRes.data.forEach(p => { map[p.influencer_id] = p; });
          setTiktokProfiles(prev => append ? { ...prev, ...map } : map);
        }
        if (ytRes.data) {
          const map: Record<string, any> = {};
          ytRes.data.forEach(p => { map[p.influencer_id] = p; });
          setYoutubeProfiles(prev => append ? { ...prev, ...map } : map);
        }
        if (snapRes.data) {
          // Compute avg spotlight views and count from snapchat_spotlights
          const spotlightsByInf = new Map<string, number[]>();
          for (const sp of (spotlightsRes.data || [])) {
            if (!spotlightsByInf.has(sp.influencer_id)) spotlightsByInf.set(sp.influencer_id, []);
            spotlightsByInf.get(sp.influencer_id)!.push(sp.views || 0);
          }
          const map: Record<string, any> = {};
          snapRes.data.forEach(p => {
            const allViews = spotlightsByInf.get(p.influencer_id) || [];
            const avgSpViews = allViews.length > 0 ? Math.round(allViews.reduce((a, b) => a + b, 0) / allViews.length) : 0;
            map[p.influencer_id] = { ...p, avg_views: avgSpViews, spotlight_count: allViews.length };
          });
          setSnapchatProfiles(prev => append ? { ...prev, ...map } : map);
        }
      }

      setPage(pageNum);
    } catch (error) {
      console.error('Error loading influencers:', error);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
      loadingRef.current = false;
    }
  }, [PAGE_SIZE, search, platformFilter, categoryFilter, minFollowers, maxFollowers, minEngagement, maxEngagement]);

  useEffect(() => {
    setPage(0);
    setHasMore(true);
    loadInfluencers(0, false);
  }, [search, platformFilter, categoryFilter, minFollowers, maxFollowers, minEngagement, maxEngagement, loadInfluencers]);

  useEffect(() => {
    loadAddedInfluencers();
    loadRecruitedInfluencers();
    loadUserCredits();
  }, [currentUser, userProfile?.agency_id]);

  useEffect(() => {
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLElement;
      if (!target) return;

      const scrollPosition = target.scrollTop + target.clientHeight;
      const scrollThreshold = target.scrollHeight - 800;

      if (!isLoadingMore && hasMore && scrollPosition >= scrollThreshold) {
        console.log('Loading more influencers...', { page: page + 1, hasMore, isLoadingMore, scrollPosition, scrollThreshold });
        loadInfluencers(page + 1, true);
      }
    };

    const mainElement = document.querySelector('main');
    if (mainElement) {
      mainElement.addEventListener('scroll', handleScroll, { passive: true });
      return () => mainElement.removeEventListener('scroll', handleScroll);
    }
  }, [page, isLoadingMore, hasMore, loadInfluencers]);

  const handleAddToOrganization = async (influencerId: string) => {
    if (!currentUser || !userProfile?.agency_id) {
      console.log('No current user or agency, cannot add influencer');
      showNotification('Vous devez être connecté pour effectuer cette action', 'error');
      return;
    }

    if (addedInfluencers.has(influencerId)) {
      try {
        const { data: existingRecords, error: fetchError } = await supabase
          .from('organization_influencers')
          .select('id')
          .eq('organization_id', userProfile.agency_id)
          .eq('influencer_id', influencerId);

        if (fetchError) throw fetchError;

        if (existingRecords && existingRecords.length > 0) {
          const { error: deleteError } = await supabase
            .from('organization_influencers')
            .delete()
            .eq('id', existingRecords[0].id);

          if (deleteError) throw deleteError;

          const newSet = new Set(addedInfluencers);
          newSet.delete(influencerId);
          setAddedInfluencers(newSet);
          showNotification('Influenceur retiré de votre organisation', 'success');
          console.log('Successfully removed from organization');
        }
      } catch (error) {
        console.error('Error removing influencer from organization:', error);
        showNotification('Erreur lors du retrait de l\'influenceur', 'error');
      }
    } else {
      console.log('Adding influencer:', influencerId, 'for agency:', userProfile.agency_id);

      try {
        const { data, error } = await supabase
          .from('organization_influencers')
          .insert({
            organization_id: userProfile.agency_id,
            influencer_id: influencerId,
            added_by: currentUser.id
          })
          .select();

        if (error) {
          console.error('Supabase error:', error);
          throw error;
        }

        console.log('Successfully added:', data);
        setAddedInfluencers(prev => new Set([...prev, influencerId]));
        showNotification('Influenceur ajouté à votre organisation', 'success');
      } catch (error) {
        console.error('Error adding influencer to organization:', error);
        showNotification('Erreur lors de l\'ajout de l\'influenceur', 'error');
      }
    }
  };

  const handleAddToRecruitment = async (influencer: InfluencerData) => {
    if (!currentUser || !userProfile?.agency_id) {
      console.log('No current user or agency, cannot add to recruitment');
      showNotification('Vous devez être connecté pour effectuer cette action', 'error');
      return;
    }

    if (recruitedInfluencers.has(influencer.id)) {
      try {
        const { data: existingRecords, error: fetchError } = await supabase
          .from('recruitment_influencers')
          .select('id')
          .eq('agency_id', userProfile.agency_id)
          .eq('influencer_id', influencer.id);

        if (fetchError) throw fetchError;

        if (existingRecords && existingRecords.length > 0) {
          const { error: deleteError } = await supabase
            .from('recruitment_influencers')
            .delete()
            .eq('id', existingRecords[0].id);

          if (deleteError) throw deleteError;

          const newSet = new Set(recruitedInfluencers);
          newSet.delete(influencer.id);
          setRecruitedInfluencers(newSet);
          showNotification('Influenceur retiré du recrutement', 'success');
          console.log('Successfully removed from recruitment');
        }
      } catch (error) {
        console.error('Error removing influencer from recruitment:', error);
        showNotification('Erreur lors du retrait de l\'influenceur', 'error');
      }
    } else {
      try {
        const { data, error } = await supabase
          .from('recruitment_influencers')
          .insert({
            influencer_id: influencer.id,
            agency_id: userProfile.agency_id,
            full_name: influencer.display_name || influencer.name || influencer.username,
            username: influencer.username,
            instagram_url: influencer.profile_url || `https://instagram.com/${influencer.username}`,
            profile_picture: influencer.profile_image_url,
            internal_rating: 3,
            tags: influencer.category ? [influencer.category] : [],
            collaborated_brands: '',
            status: 'spotted',
            created_by: currentUser?.id
          })
          .select();

        if (error) {
          console.error('Supabase error:', error);
          throw error;
        }

        console.log('Successfully added to recruitment:', data);
        setRecruitedInfluencers(prev => new Set([...prev, influencer.id]));
        showNotification('Influenceur ajouté au recrutement', 'success');
      } catch (error) {
        console.error('Error adding influencer to recruitment:', error);
        showNotification('Erreur lors de l\'ajout de l\'influenceur', 'error');
      }
    }
  };

  const getInitials = (name: string | null | undefined) => {
    if (!name || name.trim() === '') return '?';
    const parts = name.trim().split(' ').filter(p => p.length > 0);
    if (parts.length === 0) return '?';
    return parts.map(n => n[0]).join('').slice(0, 2).toUpperCase();
  };

  const getDisplayName = (influencer: InfluencerData) => {
    return influencer.display_name || influencer.name || influencer.username || 'Inconnu';
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const filteredInfluencers = influencers
    .filter(influencer => {
      if (search.trim()) {
        const searchLower = search.toLowerCase().trim();
        const username = influencer.username?.toLowerCase() || '';
        const displayName = (influencer.display_name || influencer.name || '').toLowerCase();

        return username.includes(searchLower) ||
               `@${username}`.includes(searchLower) ||
               displayName.includes(searchLower);
      }
      return true;
    })
    .filter(influencer => (influencer.collabs_count || 0) >= minCollabs)
    .sort((a, b) => {
      switch (sortBy) {
        case 'followers':
          return b.followers_count - a.followers_count;
        case 'engagement':
          return (b.engagement_rate || 0) - (a.engagement_rate || 0);
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        default:
          return 0;
      }
    });

  const categories = [...new Set(influencers.map(i => i.category).filter(Boolean))];
  const platforms = [...new Set(influencers.map(i => i.platform).filter(Boolean))];

  const InfluencerAvatar = ({ influencer, onRefresh }: { influencer: InfluencerData; onRefresh?: () => void }) => {
    const [imageError, setImageError] = useState(false);
    const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const displayName = getDisplayName(influencer);

    useEffect(() => {
      if (!influencer.profile_image_url) {
        setLoading(false);
        return;
      }

      setImageDataUrl(influencer.profile_image_url);
      setLoading(false);
    }, [influencer.profile_image_url]);

    if (!influencer.profile_image_url || imageError) {
      return (
        <div className="relative group/refresh">
          <div className="w-12 h-12 rounded-full bg-white/10 text-white font-bold flex items-center justify-center text-sm border border-white/[0.10]">
            {getInitials(displayName)}
          </div>
          {imageError && onRefresh && (
            <button
              onClick={(e) => { e.stopPropagation(); onRefresh(); }}
              className="absolute -bottom-0.5 -right-0.5 w-5 h-5 bg-[#1e1e1e] border border-white/10 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors opacity-0 group-hover/refresh:opacity-100"
              title="Rafraîchir la photo de profil"
            >
              <RefreshCw className="w-2.5 h-2.5 text-white" />
            </button>
          )}
        </div>
      );
    }

    if (loading || !imageDataUrl) {
      return <div className="w-12 h-12 rounded-full bg-white/10 animate-pulse" />;
    }

    return (
      <img
        src={imageDataUrl}
        alt={displayName}
        className="w-12 h-12 rounded-full object-cover ring-2 ring-white/10 group-hover:ring-white/20 transition-all"
      />
    );
  };

  const PostImage = ({ imageUrl, postType }: { imageUrl: string | null; postType: string }) => {
    const [imageError, setImageError] = useState(false);
    const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      if (!imageUrl) {
        setLoading(false);
        return;
      }

      setImageDataUrl(imageUrl);
      setLoading(false);
    }, [imageUrl]);

    if (!imageUrl || imageError) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-white/[0.04]">
          <div className="text-center">
            <Eye className="w-6 h-6 text-white/20 mx-auto mb-1" />
            <div className="text-[11px] text-white/20">Image non disponible</div>
          </div>
        </div>
      );
    }

    if (loading || !imageDataUrl) {
      return <div className="w-full h-full bg-white/[0.06] animate-pulse" />;
    }

    return (
      <>
        <img
          src={imageDataUrl}
          alt="Post Instagram"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        {postType === 'reel' && (
          <div className="absolute top-2.5 right-2.5 bg-white/20 backdrop-blur-sm text-white px-2.5 py-1 rounded-full text-[10px] font-bold border border-white/20">
            Reel
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </>
    );
  };

  const showNotification = (message: string, type: 'success' | 'error' | 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleAddNewProfile = async () => {
    if (!newProfileUrl.trim()) {
      showNotification('Veuillez entrer une URL de profil', 'error');
      return;
    }

    if (!currentUser || !userProfile?.agency_id) {
      showNotification('Vous devez être connecté pour ajouter un profil', 'error');
      return;
    }

    // Check organization credits (1 credit required)
    const { data: creditsData, error: creditsError } = await supabase.rpc('get_organization_credits', {
      org_id: userProfile.agency_id
    });

    if (creditsError) {
      console.error('Error checking credits:', creditsError);
      showNotification('Erreur lors de la vérification des crédits', 'error');
      return;
    }

    const currentBalance = creditsData?.[0]?.balance || 0;

    if (currentBalance < 1) {
      showNotification(
        `Crédits insuffisants. Cette action nécessite 1 crédit. Solde actuel: ${currentBalance}`,
        'error'
      );
      return;
    }

    try {
      // Consume 1 credit
      const { error: consumeError } = await supabase.rpc('consume_credits', {
        org_id: userProfile.agency_id,
        amount: 1
      });

      if (consumeError) {
        console.error('Error consuming credits:', consumeError);
        showNotification('Erreur lors de la consommation des crédits', 'error');
        return;
      }

      // Update credits display
      setCreditsRemaining(currentBalance - 1);

      setIsScrapingNewProfile(true);
      showNotification('Récupération des données en cours... (1 crédit consommé)', 'info');

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/functions/v1/instagram-scraper`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': supabaseKey,
          'Authorization': `Bearer ${supabaseKey}`,
        },
        body: JSON.stringify({
          instagramUrls: [newProfileUrl.trim()],
          resultsType: 'posts',
          resultsLimit: 200
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Erreur HTTP: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success && result.influencersInserted > 0) {
        const influencer = result.influencers[0];

        const scrapePostsResponse = await fetch(`${supabaseUrl}/functions/v1/scrape-influencer-posts`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': supabaseKey,
            'Authorization': `Bearer ${supabaseKey}`,
          },
          body: JSON.stringify({
            influencerId: influencer.id,
            username: influencer.username,
            resultsLimit: 15,
          }),
        });

        if (scrapePostsResponse.ok) {
          const postResult = await scrapePostsResponse.json();
          if (postResult.success) {
            showNotification(
              `Profil ajouté avec succès ! ${postResult.totalPosts} posts récupérés dont ${postResult.collaborationPosts} collaborations.`,
              'success'
            );
          } else {
            showNotification('Profil ajouté mais erreur lors de la récupération des posts.', 'error');
          }
        }

        setNewProfileUrl('');

        // Ajouter le nouvel influenceur à la liste sans recharger toute la page
        const { data: newInfluencer, error: fetchError } = await supabase
          .from('influencers')
          .select('*')
          .eq('id', influencer.id)
          .maybeSingle();

        if (!fetchError && newInfluencer) {
          // Récupérer le nombre de collaborations
          const { data: collabCounts, error: collabError } = await supabase
            .from('posts')
            .select('influencer_id')
            .eq('influencer_id', influencer.id)
            .eq('is_collaboration', true);

          const collabsCount = collabCounts?.length || 0;

          setInfluencers(prev => [{ ...newInfluencer, collabs_count: collabsCount }, ...prev]);
        }
      } else {
        showNotification(result.error || 'Impossible d\'ajouter le profil', 'error');
      }
    } catch (error) {
      console.error('Error adding new profile:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      showNotification(`Erreur lors de l'ajout du profil: ${errorMessage}`, 'error');
    } finally {
      setIsScrapingNewProfile(false);
    }
  };

  const handleRefreshProfile = async (influencerId: string, username: string) => {
    try {
      if (refreshingInfluencers.has(influencerId)) {
        showNotification('Ce profil est déjà en cours de rafraîchissement', 'info');
        return;
      }

      // Check organization credits (1 credit required)
      if (!userProfile?.agency_id) {
        showNotification('Organisation non trouvée', 'error');
        return;
      }

      const { data: creditsData, error: creditsError } = await supabase.rpc('get_organization_credits', {
        org_id: userProfile.agency_id
      });

      if (creditsError) {
        console.error('Error checking credits:', creditsError);
        showNotification('Erreur lors de la vérification des crédits', 'error');
        return;
      }

      const currentBalance = creditsData?.[0]?.balance || 0;

      if (currentBalance < 1) {
        showNotification(
          `Crédits insuffisants. Cette action nécessite 1 crédit. Solde actuel: ${currentBalance}`,
          'error'
        );
        return;
      }

      const { data: influencerData, error: fetchError } = await supabase
        .from('influencers')
        .select('last_updated')
        .eq('id', influencerId)
        .maybeSingle();

      if (fetchError) {
        console.error('Error fetching influencer data:', fetchError);
        showNotification('Erreur lors de la vérification du profil', 'error');
        return;
      }

      if (influencerData?.last_updated) {
        const lastUpdate = new Date(influencerData.last_updated);
        const now = new Date();
        const hoursSinceUpdate = (now.getTime() - lastUpdate.getTime()) / (1000 * 60 * 60);

        console.log('=== REFRESH CHECK ===');
        console.log('Influencer ID:', influencerId);
        console.log('Username:', username);
        console.log('Last update:', lastUpdate);
        console.log('Now:', now);
        console.log('Hours since update:', hoursSinceUpdate);

        if (hoursSinceUpdate < 24 && hoursSinceUpdate > 0) {
          const hoursRemaining = Math.ceil(24 - hoursSinceUpdate);
          showNotification(
            `Le profil a été rafraîchi il y a ${Math.floor(hoursSinceUpdate)} heures. Réessayez dans ${hoursRemaining} heures.`,
            'info'
          );
          return;
        }
      }

      // Consume 1 credit
      const { error: consumeError } = await supabase.rpc('consume_credits', {
        org_id: userProfile.agency_id,
        amount: 1
      });

      if (consumeError) {
        console.error('Error consuming credits:', consumeError);
        showNotification('Erreur lors de la consommation des crédits', 'error');
        return;
      }

      // Update credits display
      setCreditsRemaining(currentBalance - 1);

      setRefreshingInfluencers(prev => new Set(prev).add(influencerId));
      showNotification('Rafraîchissement en cours... Cela peut prendre jusqu\'à 2 minutes. (1 crédit consommé)', 'info');

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const profileUrl = `https://www.instagram.com/${username}/`;

      const response = await fetch(`${supabaseUrl}/functions/v1/instagram-scraper`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': supabaseKey,
          'Authorization': `Bearer ${supabaseKey}`,
        },
        body: JSON.stringify({
          instagramUrls: [profileUrl],
          resultsType: 'posts',
          resultsLimit: 200
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Erreur HTTP: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success && result.influencersInserted > 0) {
        const influencer = result.influencers[0];

        showNotification('Profil mis à jour, récupération des posts...', 'info');

        const scrapePostsResponse = await fetch(`${supabaseUrl}/functions/v1/scrape-influencer-posts`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': supabaseKey,
            'Authorization': `Bearer ${supabaseKey}`,
          },
          body: JSON.stringify({
            influencerId: influencer.id,
            username: influencer.username,
            resultsLimit: 15,
          }),
        });

        if (scrapePostsResponse.ok) {
          const postResult = await scrapePostsResponse.json();
          if (postResult.success) {
            showNotification(
              `Profil rafraîchi avec succès ! ${postResult.totalPosts} posts récupérés dont ${postResult.collaborationPosts} collaborations.`,
              'success'
            );

            // Lancer l'analyse IA en arrière-plan (sans bloquer l'UI)
            if (postResult.collaborationPosts > 0) {
              console.log('[DISCOVER] Launching AI analysis in background...');
              import('../lib/creatorAnalysis').then(({ runCompleteAnalysis }) => {
                runCompleteAnalysis(influencer.id)
                  .then(results => {
                    console.log('[DISCOVER] AI analysis completed:', results);
                  })
                  .catch(err => {
                    console.error('[DISCOVER] AI analysis failed:', err);
                  });
              });
            }
          } else {
            showNotification('Profil mis à jour mais erreur lors de la récupération des posts.', 'error');
          }
        }

        // Mettre à jour uniquement l'influenceur rafraîchi dans la liste sans recharger toute la page
        const { data: updatedInfluencer, error: updateError } = await supabase
          .from('influencers')
          .select('*')
          .eq('id', influencer.id)
          .maybeSingle();

        if (!updateError && updatedInfluencer) {
          // Récupérer le nombre de collaborations
          const { data: collabCounts, error: collabError } = await supabase
            .from('posts')
            .select('influencer_id')
            .eq('influencer_id', influencer.id)
            .eq('is_collaboration', true);

          const collabsCount = collabCounts?.length || 0;

          setInfluencers(prev => prev.map(inf =>
            inf.id === influencer.id
              ? { ...updatedInfluencer, collabs_count: collabsCount }
              : inf
          ));
        }
      } else {
        throw new Error(result.error || 'Échec du rafraîchissement');
      }
    } catch (error) {
      console.error('Error refreshing profile:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      showNotification(`Erreur lors du rafraîchissement: ${errorMessage}`, 'error');
    } finally {
      setRefreshingInfluencers(prev => {
        const newSet = new Set(prev);
        newSet.delete(influencerId);
        return newSet;
      });
    }
  };

  const handleViewPosts = async (influencer: InfluencerData) => {
    setSelectedInfluencer(influencer);
    setLoadingPosts(true);
    setPosts([]);

    try {
      console.log('Loading posts for influencer:', influencer.id, influencer.username);

      const { data: existingPosts, error: fetchError } = await supabase
        .from('posts')
        .select('*')
        .eq('influencer_id', influencer.id)
        .order('timestamp', { ascending: false });

      console.log('Existing posts found:', existingPosts?.length || 0, existingPosts);

      if (fetchError) throw fetchError;

      if (existingPosts && existingPosts.length > 0) {
        setPosts(existingPosts);
        setLoadingPosts(false);

        const totalLikes = existingPosts.reduce((sum, p) => sum + (p.likes_count || 0), 0);
        const totalComments = existingPosts.reduce((sum, p) => sum + (p.comments_count || 0), 0);
        const avgLikes = Math.round(totalLikes / existingPosts.length);
        const avgComments = Math.round(totalComments / existingPosts.length);

        if (influencer.followers_count > 0) {
          const totalEngagement = totalLikes + totalComments;
          const avgEngagementPerPost = totalEngagement / existingPosts.length;
          const engagementRate = (avgEngagementPerPost / influencer.followers_count) * 100;

          await supabase
            .from('influencers')
            .update({
              engagement_rate: Number(engagementRate.toFixed(2)),
              avg_likes: avgLikes,
              avg_comments: avgComments,
              posts_count: existingPosts.length
            })
            .eq('id', influencer.id);

          setInfluencers(prev => prev.map(inf =>
            inf.id === influencer.id
              ? { ...inf, engagement_rate: Number(engagementRate.toFixed(2)), posts_count: existingPosts.length }
              : inf
          ));
        }
      } else {
        setPosts([]);
        setLoadingPosts(false);
      }
    } catch (error) {
      console.error('Error loading posts:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      alert(`Erreur lors du chargement des posts: ${errorMessage}`);
      setLoadingPosts(false);
    }
  };

  const LoadingSkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="rounded-2xl border border-white/[0.08] bg-white/[0.04] p-5 animate-pulse">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-full bg-white/10" />
            <div className="flex-1 space-y-2">
              <div className="h-3.5 bg-white/10 rounded w-3/4" />
              <div className="h-3 bg-white/10 rounded w-1/2" />
            </div>
          </div>
          <div className="mt-4 space-y-2">
            <div className="h-3 bg-white/10 rounded" />
            <div className="h-3 bg-white/10 rounded w-5/6" />
          </div>
          <div className="grid grid-cols-2 gap-2 mt-4">
            <div className="h-14 bg-white/10 rounded-xl" />
            <div className="h-14 bg-white/10 rounded-xl" />
          </div>
        </div>
      ))}
    </div>
  );

  const EmptyState = () => (
    <div className="text-center py-20">
      <div className="w-16 h-16 rounded-2xl bg-white/[0.05] flex items-center justify-center mx-auto mb-4">
        <Search className="w-8 h-8 text-white/20" />
      </div>
      <h3 className="text-base font-semibold text-white/60 mb-1">Aucun influenceur trouvé</h3>
      <p className="text-white/30 text-sm">Modifiez vos critères de recherche ou vos filtres</p>
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Notification Toast */}
      {notification && (
        <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top-2 duration-300">
          <div
            className={`rounded-2xl shadow-2xl p-4 pr-12 max-w-md border backdrop-blur-xl ${
              notification.type === 'success'
                ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300'
                : notification.type === 'error'
                ? 'bg-red-500/15 border-red-500/30 text-red-300'
                : 'bg-blue-500/15 border-blue-500/30 text-blue-300'
            }`}
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                {notification.type === 'success' && <Check className="w-4 h-4 text-emerald-400" />}
                {notification.type === 'error' && <X className="w-4 h-4 text-red-400" />}
                {notification.type === 'info' && <TrendingUp className="w-4 h-4 text-blue-400" />}
              </div>
              <p className="text-sm font-medium flex-1">{notification.message}</p>
              <button
                onClick={() => setNotification(null)}
                className="absolute top-3 right-3 text-white/30 hover:text-white/60 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white tracking-tight">Discover</h1>
          <p className="text-white/40 text-xs mt-0.5">Base de données influenceurs</p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2.5 bg-white/[0.06] border border-white/[0.10] rounded-2xl">
          <Coins className="w-4 h-4 text-amber-400" />
          <div className="text-left">
            <p className="text-[10px] font-semibold text-white/40 uppercase tracking-widest leading-none">Crédits</p>
            <p className="text-base font-bold text-white leading-tight flex items-center">{creditsRemaining >= 999999 ? <Infinity className="w-4 h-4" /> : creditsRemaining}</p>
          </div>
        </div>
      </div>

      {/* Filters panel */}
      <div className="rounded-2xl border border-white/[0.08] bg-white/[0.04] overflow-visible">
        <div className="p-5 space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <input
              type="text"
              placeholder="Rechercher par nom, username ou catégorie..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full h-11 rounded-xl border border-white/[0.08] bg-white/[0.06] px-4 pl-11 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/20 focus:bg-white/[0.08] transition-all"
            />
          </div>

          {/* Add New Profile */}
          <div className="space-y-2.5">
            <div className="flex items-start gap-2 px-3.5 py-2.5 bg-blue-500/[0.08] border border-blue-500/20 rounded-xl">
              <TrendingUp className="w-3.5 h-3.5 text-blue-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-blue-300/80 leading-relaxed">
                <span className="font-semibold text-blue-300">Influenceur manquant ?</span> Ajoutez-le manuellement ci-dessous via son URL Instagram.
              </p>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Ajouter un profil Instagram (URL complète)..."
                value={newProfileUrl}
                onChange={(e) => setNewProfileUrl(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !isScrapingNewProfile) handleAddNewProfile();
                }}
                disabled={isScrapingNewProfile}
                className="flex-1 h-10 rounded-xl border border-white/[0.08] bg-white/[0.06] px-4 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/20 transition-all disabled:opacity-50"
              />
              <button
                onClick={handleAddNewProfile}
                disabled={isScrapingNewProfile || !newProfileUrl.trim()}
                className="flex items-center gap-1.5 px-4 h-10 bg-white text-black rounded-xl hover:bg-white/90 transition-all disabled:opacity-40 disabled:cursor-not-allowed font-semibold text-sm"
              >
                {isScrapingNewProfile ? (
                  <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                {isScrapingNewProfile ? 'Ajout...' : 'Ajouter'}
              </button>
            </div>
          </div>

          {/* Main Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <CustomListbox
              value={platformFilter}
              onChange={setPlatformFilter}
              options={[
                { value: 'all', label: 'Toutes plateformes' },
                ...platforms.map(p => ({ value: p, label: p }))
              ]}
              className="h-10"
            />
            <CustomListbox
              value={sortBy}
              onChange={setSortBy}
              options={[
                { value: 'followers', label: 'Plus populaires' },
                { value: 'engagement', label: 'Plus engageants' },
                { value: 'name', label: 'Ordre alphabétique' }
              ]}
              className="h-10"
            />
          </div>

          {/* Advanced Filters Toggle */}
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className="flex items-center gap-1.5 text-xs font-semibold text-white/40 hover:text-white/70 transition-colors"
          >
            <Filter className="w-3.5 h-3.5" />
            {showAdvancedFilters ? 'Masquer les filtres avancés' : 'Filtres avancés'}
          </button>

          {/* Advanced Filters */}
          {showAdvancedFilters && (
            <div className="space-y-5 pt-4 border-t border-white/[0.06]">
              {/* Followers Range */}
              <div className="space-y-2.5">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] font-bold text-white/50 uppercase tracking-widest">Followers</label>
                  <span className="text-xs font-bold text-blue-400">
                    {formatNumber(minFollowers)} — {maxFollowers >= 10000000 ? '10M+' : formatNumber(maxFollowers)}
                  </span>
                </div>
                <div className="space-y-1.5">
                  {[
                    { value: convertToLog(minFollowers), onChange: (v: number) => setMinFollowers(convertToLinear(v)) },
                    { value: convertToLog(maxFollowers), onChange: (v: number) => setMaxFollowers(convertToLinear(v)) },
                  ].map((slider, idx) => (
                    <div key={idx} className="relative py-1.5">
                      <div className="h-1.5 bg-white/[0.08] rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500/60 transition-all duration-200"
                          style={{ width: `${((slider.value - Math.log(1000)) / (Math.log(10000000) - Math.log(1000))) * 100}%` }} />
                      </div>
                      <div className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-white rounded-full shadow-md pointer-events-none"
                        style={{ left: `calc(${((slider.value - Math.log(1000)) / (Math.log(10000000) - Math.log(1000))) * 100}% - 7px)` }} />
                      <input type="range" min={Math.log(1000)} max={Math.log(10000000)} step="0.01"
                        value={slider.value} onChange={(e) => slider.onChange(Number(e.target.value))}
                        className="absolute inset-0 w-full opacity-0 cursor-pointer" />
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="text-[10px] text-white/30 mb-1 block">Min</label>
                    <input type="number" value={minFollowers} onChange={(e) => setMinFollowers(Number(e.target.value))}
                      className="w-full h-8 rounded-lg border border-white/[0.08] bg-white/[0.06] px-2.5 text-xs text-white focus:outline-none focus:border-white/20" placeholder="0" />
                  </div>
                  <div className="flex-1">
                    <label className="text-[10px] text-white/30 mb-1 block">Max</label>
                    <input type="number" value={maxFollowers} onChange={(e) => setMaxFollowers(Number(e.target.value))}
                      className="w-full h-8 rounded-lg border border-white/[0.08] bg-white/[0.06] px-2.5 text-xs text-white focus:outline-none focus:border-white/20" placeholder="10000000" />
                  </div>
                </div>
              </div>

              {/* Engagement Rate */}
              <div className="space-y-2.5">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] font-bold text-white/50 uppercase tracking-widest">Engagement</label>
                  <span className="text-xs font-bold text-emerald-400">{minEngagement}% — {maxEngagement}%</span>
                </div>
                <div className="space-y-1.5">
                  {[
                    { value: minEngagement, onChange: setMinEngagement },
                    { value: maxEngagement, onChange: setMaxEngagement },
                  ].map((slider, idx) => (
                    <div key={idx} className="relative py-1.5">
                      <div className="h-1.5 bg-white/[0.08] rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500/60 transition-all duration-200" style={{ width: `${slider.value}%` }} />
                      </div>
                      <div className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-white rounded-full shadow-md pointer-events-none"
                        style={{ left: `calc(${slider.value}% - 7px)` }} />
                      <input type="range" min="0" max="100" step="0.5" value={slider.value}
                        onChange={(e) => slider.onChange(Number(e.target.value))}
                        className="absolute inset-0 w-full opacity-0 cursor-pointer" />
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="text-[10px] text-white/30 mb-1 block">Min (%)</label>
                    <input type="number" value={minEngagement} onChange={(e) => setMinEngagement(Number(e.target.value))} min="0" max="100" step="0.5"
                      className="w-full h-8 rounded-lg border border-white/[0.08] bg-white/[0.06] px-2.5 text-xs text-white focus:outline-none focus:border-white/20" placeholder="0" />
                  </div>
                  <div className="flex-1">
                    <label className="text-[10px] text-white/30 mb-1 block">Max (%)</label>
                    <input type="number" value={maxEngagement} onChange={(e) => setMaxEngagement(Number(e.target.value))} min="0" max="100" step="0.5"
                      className="w-full h-8 rounded-lg border border-white/[0.08] bg-white/[0.06] px-2.5 text-xs text-white focus:outline-none focus:border-white/20" placeholder="100" />
                  </div>
                </div>
              </div>

              {/* Collabs minimum */}
              <div className="space-y-2.5">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] font-bold text-white/50 uppercase tracking-widest">Collabs minimum</label>
                  <span className="text-xs font-bold text-amber-400">{minCollabs}+</span>
                </div>
                <div className="relative py-1.5">
                  <div className="h-1.5 bg-white/[0.08] rounded-full overflow-hidden">
                    <div className="h-full bg-amber-500/60 transition-all duration-200" style={{ width: `${(minCollabs / 50) * 100}%` }} />
                  </div>
                  <div className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-white rounded-full shadow-md pointer-events-none"
                    style={{ left: `calc(${(minCollabs / 50) * 100}% - 7px)` }} />
                  <input type="range" min="0" max="50" step="1" value={minCollabs}
                    onChange={(e) => setMinCollabs(Number(e.target.value))}
                    className="absolute inset-0 w-full opacity-0 cursor-pointer" />
                </div>
                <input type="number" value={minCollabs} onChange={(e) => setMinCollabs(Number(e.target.value))} min="0"
                  className="w-full h-8 rounded-lg border border-white/[0.08] bg-white/[0.06] px-2.5 text-xs text-white focus:outline-none focus:border-white/20" placeholder="0" />
              </div>

              <button
                onClick={() => {
                  setMinFollowers(0); setMaxFollowers(10000000);
                  setMinEngagement(0); setMaxEngagement(100);
                  setMinCollabs(0); setPlatformFilter('all');
                  setCategoryFilter('all'); setSearch('');
                }}
                className="w-full px-4 py-2.5 bg-white/[0.06] hover:bg-white/[0.10] text-white/60 hover:text-white rounded-xl transition-all text-xs font-semibold border border-white/[0.08]"
              >
                Réinitialiser tous les filtres
              </button>
            </div>
          )}
        </div>

        {/* Active Filters Summary */}
        {(minFollowers > 0 || maxFollowers < 10000000 || minEngagement > 0 || maxEngagement < 100 || minCollabs > 0) && (
          <div className="px-5 py-3 border-t border-white/[0.06] flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-semibold text-white/30 uppercase tracking-widest">Filtres</span>
            {minFollowers > 0 && (
              <span className="px-2.5 py-1 bg-blue-500/10 border border-blue-500/20 rounded-full text-[11px] font-semibold text-blue-400">
                Followers ≥ {formatNumber(minFollowers)}
              </span>
            )}
            {maxFollowers < 10000000 && (
              <span className="px-2.5 py-1 bg-blue-500/10 border border-blue-500/20 rounded-full text-[11px] font-semibold text-blue-400">
                Followers ≤ {formatNumber(maxFollowers)}
              </span>
            )}
            {minEngagement > 0 && (
              <span className="px-2.5 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-[11px] font-semibold text-emerald-400">
                Engagement ≥ {minEngagement}%
              </span>
            )}
            {maxEngagement < 100 && (
              <span className="px-2.5 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-[11px] font-semibold text-emerald-400">
                Engagement ≤ {maxEngagement}%
              </span>
            )}
            {minCollabs > 0 && (
              <span className="px-2.5 py-1 bg-amber-500/10 border border-amber-500/20 rounded-full text-[11px] font-semibold text-amber-400">
                Collabs ≥ {minCollabs}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Grid */}
      {isLoading ? (
        <LoadingSkeleton />
      ) : filteredInfluencers.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <p className="text-[11px] text-white/30 font-medium">{filteredInfluencers.length} influenceur{filteredInfluencers.length > 1 ? 's' : ''}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredInfluencers.map((influencer) => (
              <div
                key={influencer.id}
                className="group rounded-2xl border border-white/[0.08] bg-white/[0.04] hover:border-white/[0.16] hover:bg-white/[0.07] p-4 transition-all duration-200 cursor-default"
              >
                {/* Header */}
                <div className="flex items-start gap-3 mb-3">
                  <div className="relative flex-shrink-0">
                    <InfluencerAvatar
                      influencer={influencer}
                      onRefresh={() => handleRefreshProfile(influencer.id, influencer.username)}
                    />
                    {influencer.verified && (
                      <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center border-2 border-[#0a0a0a]">
                        <Award className="w-2.5 h-2.5 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0 pt-0.5">
                    <h3 className="font-bold text-white truncate text-sm">{getDisplayName(influencer)}</h3>
                    <p className="text-xs text-white/40 truncate">@{influencer.username}</p>
                  </div>
                </div>

                {/* Bio */}
                {influencer.bio && (
                  <p className="text-xs text-white/40 line-clamp-2 mb-3 leading-relaxed">
                    {influencer.bio}
                  </p>
                )}

                {/* Platform tabs */}
                {(() => {
                  const ttProfile = tiktokProfiles[influencer.id];
                  const ytProfile = youtubeProfiles[influencer.id];
                  const snapProfile = snapchatProfiles[influencer.id];
                  const hasExtra = ttProfile || ytProfile || snapProfile;
                  const activeTab = platformTabs[influencer.id] || 'instagram';

                  const followers = activeTab === 'tiktok' && ttProfile ? ttProfile.followers_count
                    : activeTab === 'youtube' && ytProfile ? ytProfile.subscribers_count
                    : activeTab === 'snapchat' && snapProfile ? (snapProfile.subscriber_count || 0)
                    : influencer.followers_count;

                  const engagement = activeTab === 'tiktok' && ttProfile ? ttProfile.engagement_rate
                    : activeTab === 'youtube' && ytProfile ? ytProfile.engagement_rate
                    : activeTab === 'snapchat' ? null
                    : influencer.engagement_rate;

                  const avgViews = activeTab === 'tiktok' && ttProfile ? ttProfile.avg_views
                    : activeTab === 'youtube' && ytProfile ? ytProfile.avg_views
                    : activeTab === 'snapchat' && snapProfile ? (snapProfile.avg_views || 0)
                    : influencer.avg_views;

                  const snapSpotlights = activeTab === 'snapchat' && snapProfile ? (snapProfile.spotlight_count || 0) : null;

                  return (
                    <>
                      {hasExtra && (
                        <div className="flex gap-1 mb-3 flex-wrap">
                          <button
                            onClick={(e) => { e.stopPropagation(); setPlatformTabs(p => ({ ...p, [influencer.id]: 'instagram' })); }}
                            className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold transition-all ${
                              activeTab === 'instagram' ? 'bg-gradient-to-r from-[#833AB4] to-[#E1306C] text-white' : 'bg-white/[0.06] text-white/40 hover:text-white/60'
                            }`}
                          >
                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                            IG
                          </button>
                          {ttProfile && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setPlatformTabs(p => ({ ...p, [influencer.id]: 'tiktok' })); }}
                              className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold transition-all ${
                                activeTab === 'tiktok' ? 'bg-black text-white' : 'bg-white/[0.06] text-white/40 hover:text-white/60'
                              }`}
                            >
                              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/></svg>
                              TT
                            </button>
                          )}
                          {ytProfile && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setPlatformTabs(p => ({ ...p, [influencer.id]: 'youtube' })); }}
                              className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold transition-all ${
                                activeTab === 'youtube' ? 'bg-[#FF0000] text-white' : 'bg-white/[0.06] text-white/40 hover:text-white/60'
                              }`}
                            >
                              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                              YT
                            </button>
                          )}
                          {snapProfile && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setPlatformTabs(p => ({ ...p, [influencer.id]: 'snapchat' })); }}
                              className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold transition-all ${
                                activeTab === 'snapchat' ? 'bg-[#FFFC00] text-black' : 'bg-white/[0.06] text-white/40 hover:text-white/60'
                              }`}
                            >
                              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M12.166 2C9.036 2 7.07 3.686 6.326 5.956c-.199.6-.165 1.817-.148 2.535l-.017.003c-.27.044-.57.028-.88-.02-.277-.044-.54-.11-.77-.11-.43 0-.814.168-.99.5-.13.244-.13.524.02.784.31.544 1.05.946 2.09 1.134.014.006.025.01.038.016-.016.053-.033.107-.05.162-.2.65-.557 1.24-1.09 1.617-.43.302-.644.782-.547 1.252.08.393.38.72.81.876.246.089.53.128.842.114.215-.01.44-.04.672-.082.383.636 1.044 1.082 2.098 1.266.88.153 1.91.128 2.72.12h.008c.81.008 1.84.033 2.72-.12 1.054-.184 1.715-.63 2.098-1.266.232.042.457.072.672.082.312.014.596-.025.842-.114.43-.156.73-.483.81-.876.097-.47-.117-.95-.547-1.252-.533-.377-.89-.966-1.09-1.617a5.49 5.49 0 01-.05-.162c.013-.006.024-.01.038-.016 1.04-.188 1.78-.59 2.09-1.134.15-.26.15-.54.02-.784-.176-.332-.56-.5-.99-.5-.23 0-.493.066-.77.11-.31.048-.61.064-.88.02l-.017-.003c.017-.718.051-1.935-.148-2.535C16.93 3.686 14.964 2 11.834 2z"/></svg>
                              SC
                            </button>
                          )}
                        </div>
                      )}

                      {/* Stats grid */}
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <div className="rounded-xl bg-white/[0.05] border border-white/[0.06] p-2.5 text-center">
                          <div className="flex items-center justify-center gap-1 mb-1">
                            {activeTab === 'instagram' && !hasExtra && (
                              <svg className="w-2.5 h-2.5 text-white/20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                            )}
                            <p className="text-[9px] text-white/30 font-semibold uppercase tracking-widest">Followers</p>
                          </div>
                          <p className="font-bold text-sm text-white">{formatNumber(followers)}</p>
                        </div>
                        <div className="rounded-xl bg-emerald-500/[0.07] border border-emerald-500/[0.12] p-2.5 text-center relative group/engagement">
                          <p className="text-[9px] text-emerald-400/70 font-semibold uppercase tracking-widest mb-1">Engagement</p>
                          {engagement ? (
                            <p className="font-bold text-sm text-emerald-400">{Number(engagement).toFixed(1)}%</p>
                          ) : activeTab === 'snapchat' ? (
                            <p className="font-bold text-sm text-white/20">—</p>
                          ) : (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleRefreshProfile(influencer.id, influencer.username); }}
                              disabled={refreshingInfluencers.has(influencer.id)}
                              className="text-[10px] px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded-lg hover:bg-emerald-500/30 transition-colors disabled:opacity-50"
                            >
                              {refreshingInfluencers.has(influencer.id) ? 'Calcul...' : 'Calculer'}
                            </button>
                          )}
                          <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 px-2.5 py-1.5 bg-[#1a1a1a] border border-white/[0.10] text-white text-[10px] rounded-lg whitespace-nowrap opacity-0 group-hover/engagement:opacity-100 pointer-events-none transition-opacity z-10">
                            {engagement ? 'Sur les 15 derniers posts' : 'Cliquez pour calculer'}
                            <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-[#1a1a1a]"></div>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mb-3.5">
                        {activeTab === 'snapchat' ? (
                          <>
                            <div className="rounded-xl bg-yellow-500/[0.07] border border-yellow-500/[0.12] p-2.5 text-center">
                              <p className="text-[9px] text-yellow-400/70 font-semibold uppercase tracking-widest mb-1">Vues moy.</p>
                              <p className="font-bold text-sm text-yellow-400">
                                {avgViews > 0 ? formatNumber(avgViews) : '—'}
                              </p>
                            </div>
                            <div className="rounded-xl bg-yellow-500/[0.07] border border-yellow-500/[0.12] p-2.5 text-center">
                              <p className="text-[9px] text-yellow-400/70 font-semibold uppercase tracking-widest mb-1">Spotlights</p>
                              <p className="font-bold text-sm text-yellow-400">
                                {snapSpotlights !== null && snapSpotlights > 0 ? snapSpotlights : '—'}
                              </p>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="rounded-xl bg-amber-500/[0.07] border border-amber-500/[0.12] p-2.5 text-center">
                              <p className="text-[9px] text-amber-400/70 font-semibold uppercase tracking-widest mb-1">Collabs</p>
                              <p className="font-bold text-sm text-amber-400">{influencer.collabs_count || 0}</p>
                            </div>
                            <div className="rounded-xl bg-blue-500/[0.07] border border-blue-500/[0.12] p-2.5 text-center relative group/views">
                              <p className="text-[9px] text-blue-400/70 font-semibold uppercase tracking-widest mb-1">Vues moy.</p>
                              <p className="font-bold text-sm text-blue-400">
                                {avgViews > 0 ? formatNumber(avgViews) : '—'}
                              </p>
                              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 px-2.5 py-1.5 bg-[#1a1a1a] border border-white/[0.10] text-white text-[10px] rounded-lg whitespace-nowrap opacity-0 group-hover/views:opacity-100 pointer-events-none transition-opacity z-10">
                                Moyenne de vues sur les Reels
                                <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-[#1a1a1a]"></div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </>
                  );
                })()}

                {/* Actions */}
                <div className="flex gap-1.5">
                  {influencer.profile_url && (
                    <a
                      href={influencer.profile_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-white text-black rounded-xl hover:bg-white/90 transition-all text-xs font-bold"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      Profil
                    </a>
                  )}
                  <button
                    onClick={() => handleViewPosts(influencer)}
                    className="p-2 bg-white/[0.08] hover:bg-white/[0.14] text-white/60 hover:text-white rounded-xl transition-all"
                    title="Voir les collaborations"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleRefreshProfile(influencer.id, influencer.username); }}
                    disabled={refreshingInfluencers.has(influencer.id)}
                    className={`p-2 rounded-xl transition-all ${
                      refreshingInfluencers.has(influencer.id)
                        ? 'bg-white/[0.04] text-white/20 cursor-not-allowed'
                        : 'bg-white/[0.08] hover:bg-white/[0.14] text-white/60 hover:text-white'
                    }`}
                    title="Rafraîchir le profil"
                  >
                    <RefreshCw className={`w-4 h-4 ${refreshingInfluencers.has(influencer.id) ? 'animate-spin' : ''}`} />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleAddToRecruitment(influencer); }}
                    className={`p-2 rounded-xl transition-all ${
                      recruitedInfluencers.has(influencer.id)
                        ? 'bg-amber-500/20 text-amber-400'
                        : 'bg-white/[0.08] hover:bg-amber-500/20 text-white/60 hover:text-amber-400'
                    }`}
                    title={recruitedInfluencers.has(influencer.id) ? 'Retirer du recrutement' : 'Ajouter au recrutement'}
                  >
                    {recruitedInfluencers.has(influencer.id) ? <Check className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleAddToOrganization(influencer.id); }}
                    className={`p-2 rounded-xl transition-all ${
                      addedInfluencers.has(influencer.id)
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : 'bg-white/[0.08] hover:bg-emerald-500/20 text-white/60 hover:text-emerald-400'
                    }`}
                    title={addedInfluencers.has(influencer.id) ? 'Retirer de mon organisation' : 'Ajouter à mon organisation'}
                  >
                    {addedInfluencers.has(influencer.id) ? <Check className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Loading more */}
          {isLoadingMore && (
            <div className="flex justify-center py-6">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-white/10 border-t-white/50 rounded-full animate-spin" />
                <span className="text-white/40 text-sm">Chargement...</span>
              </div>
            </div>
          )}

          {!hasMore && influencers.length > 0 && (
            <div className="text-center py-6">
              <p className="text-white/25 text-xs">{influencers.length} influenceurs chargés</p>
            </div>
          )}
        </>
      )}

      {/* Posts modal */}
      {selectedInfluencer && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={() => setSelectedInfluencer(null)}>
          <div
            className="relative w-full sm:max-w-5xl bg-[#141414] rounded-t-3xl sm:rounded-3xl border border-white/[0.08] overflow-hidden max-h-[92vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag handle mobile */}
            <div className="sm:hidden flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-white/20 rounded-full" />
            </div>

            {/* Header */}
            <div className="relative px-5 py-4 border-b border-white/[0.06] flex items-center gap-4 flex-shrink-0">
              <button
                onClick={() => setSelectedInfluencer(null)}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4 text-white" />
              </button>
              <div className="relative flex-shrink-0">
                <InfluencerAvatar
                  influencer={selectedInfluencer}
                  onRefresh={() => handleRefreshProfile(selectedInfluencer.id, selectedInfluencer.username)}
                />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold text-white truncate">{getDisplayName(selectedInfluencer)}</h2>
                <p className="text-white/40 text-sm">@{selectedInfluencer.username}</p>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {loadingPosts ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="w-10 h-10 border-2 border-white/10 border-t-white/50 rounded-full animate-spin mb-4" />
                  <p className="text-white/50 text-sm">Chargement des posts...</p>
                </div>
              ) : posts.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-14 h-14 rounded-2xl bg-white/[0.05] flex items-center justify-center mx-auto mb-3">
                    <Hash className="w-7 h-7 text-white/20" />
                  </div>
                  <h3 className="text-base font-semibold text-white/50 mb-1">Aucun post trouvé</h3>
                  <p className="text-white/30 text-sm">Aucune collaboration détectée pour cet influenceur</p>
                </div>
              ) : (
                <div className="space-y-5">
                  <div className="flex items-center gap-2 px-4 py-3 bg-white/[0.04] border border-white/[0.06] rounded-xl">
                    <Hash className="w-4 h-4 text-blue-400" />
                    <span className="font-semibold text-white text-sm">
                      {posts.filter(p => p.is_collaboration).length} collaboration(s) détectée(s)
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {posts.filter(p => p.is_collaboration).map((post) => (
                      <div key={post.id} className="group rounded-2xl overflow-hidden border border-white/[0.08] bg-white/[0.04] hover:border-white/[0.16] transition-all duration-200">
                        <div className="aspect-square bg-white/[0.04] relative overflow-hidden">
                          <PostImage imageUrl={post.image_url} postType={post.post_type} />
                        </div>
                        <div className="p-4">
                          <div className="flex items-center gap-4 mb-3">
                            <div className="flex items-center gap-1.5 text-white/60">
                              <Heart className="w-3.5 h-3.5 text-rose-400" />
                              <span className="font-semibold text-sm">{formatNumber(post.likes_count)}</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-white/60">
                              <MessageCircle className="w-3.5 h-3.5 text-blue-400" />
                              <span className="font-semibold text-sm">{formatNumber(post.comments_count)}</span>
                            </div>
                            {post.timestamp && (
                              <div className="flex items-center gap-1 text-white/30 ml-auto">
                                <Calendar className="w-3 h-3" />
                                <span className="text-[11px]">{new Date(post.timestamp).toLocaleDateString('fr-FR')}</span>
                              </div>
                            )}
                          </div>

                          {post.caption && (
                            <p className="text-xs text-white/40 line-clamp-3 mb-3 leading-relaxed">{post.caption}</p>
                          )}

                          {post.collaboration_keywords.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-3">
                              {post.collaboration_keywords.slice(0, 3).map((keyword, idx) => (
                                <span key={idx} className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                  {keyword}
                                </span>
                              ))}
                            </div>
                          )}

                          <a
                            href={post.post_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-1.5 w-full px-3 py-2 bg-white text-black rounded-xl hover:bg-white/90 transition-all text-xs font-bold"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                            Voir sur Instagram
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
